import axios from 'axios';
import history from 'utils/history';
import { defaultNotifier } from 'functions/notificationHandler';
import localForage from 'localforage';
import { routeMapping as routes } from './constants';
import { jsonToGraphQLQuery } from 'json-to-graphql-query'
import axiosObj from './config'
import { AUTH_BASE } from 'api/constants';
import * as jose from 'jose'
import { revokeSession } from './session';


const WORKSPACE_LOGIN = {
  token: true,
  refreshToken: true,
}

const safeDecodeJwt = (token) => {
  if (!token) {
    return null;
  }

  try {
    return jose.decodeJwt(token);
  } catch (error) {
    return null;
  }
}

/**
 * Wrapper for axios.
 * @param {string} action   - Action for axios
 * @param {string} resource - Endpoint to use
 * @param {(* | null)} [payload = null] - Data to post
 * @param {(* | null)} [config = null] - Config Axios
 */

function createAxiosInstance(unauthorizedCallback) {
  return axios.create({
    headers: {
      'content-type': 'application/json',
    },
    validateStatus: (status) => {
      const unauthorizedStatus = 401;
      if (status === unauthorizedStatus) {
        unauthorizedCallback();
      }
      return status >= 200 && status < 400;
    },
    onUploadProgress: null,
    cancelToken: null,
  });
}

async function logout() {
  const tableConfig = localStorage.getItem('table_configuration');
  const refreshToken = sessionStorage.getItem('refreshToken');
  const accessToken = sessionStorage.getItem('accessToken');
  try {
    await revokeSession(refreshToken, accessToken);
  } catch (error) {
    // Local logout should still complete if the revocation request is unavailable.
  }
  sessionStorage.removeItem('accessToken');
  sessionStorage.removeItem('refreshToken');
  sessionStorage.removeItem('userData');
  localStorage.clear();
  sessionStorage.clear();
  if (tableConfig) {
    localStorage.setItem('table_configuration', tableConfig);
  }
  // Remove YBug widget on logout
  window.dispatchEvent(new Event('ybug:cleanup'));
  await localForage.setItem('noderedModules', null);
  await localForage.setItem('hideMaintenanceBanner', false);
  history.push(routes.LOGIN.path);
  window.location.reload();
}

const checkForRefreshToken = () => {
  const requestToken = sessionStorage.getItem('refreshToken')

  if (!requestToken) {
    return logout();
  }

  return requestAccessToken({token: requestToken})
}

function isTokenExpired(error) {
  if (error && error.data && error.data.errors) {
    const { message } = error.data.errors[0];

    if (message.includes('jwt expired')
     || message.includes('invalid token')
      || message.includes('invalid signature')
      || message.includes('You are denied system access')
       || message.includes('jwt malformed')) {
          return checkForRefreshToken();
    }
    return error;
  }
}

export const axiosInstance = createAxiosInstance(logout);

function isAccessTokenExpired(state) {
  if (state && state.exp) {
    return 1000 * state.exp - (new Date()).getTime() < 5000
  }
  return true
}

function isRefreshTokenExpired(state) {
  if (state && state.exp) {
    return 1000 * state.exp - (new Date()).getTime() < 5000
  }
  return true
}

const getRequestedPermissionFromError = (error = {}) => (
  error.requiredPermission
  || error.permission
  || error.extensions?.requiredPermission
  || error.extensions?.permission
  || error.extensions?.details?.requiredPermission
  || error.extensions?.details?.permission
)

const getGraphQLOperation = (params = {}) => {
  const query = params?.data?.query
  if (!query || typeof query !== 'string') return undefined

  const match = query.match(/\b(query|mutation)\s*\{\s*([a-zA-Z0-9_]+)/)
  return match?.[2]
}

const logPermissionDenied = (params = {}, error = {}) => {
  const requestedPermission = getRequestedPermissionFromError(error)

  console.error('[403] Access denied for API request', {
    requestedPermission: requestedPermission || 'unknown',
    message: error?.message,
    code: error?.extensions?.code || error?.code,
    path: error?.path,
    operation: getGraphQLOperation(params),
    method: params?.method,
    url: params?.url,
  })
}

export default function request(params) {
  const token = sessionStorage.getItem('accessToken');
  const refreshToken = sessionStorage.getItem('refreshToken');

  const accessTokenInfo = safeDecodeJwt(token)
  const refreshTokenInfo = safeDecodeJwt(refreshToken)

  if(isAccessTokenExpired(accessTokenInfo)) {
    if (refreshToken && !isRefreshTokenExpired(refreshTokenInfo)) {
      return checkForRefreshToken()
    }

    if(token && (!refreshToken || isRefreshTokenExpired(refreshTokenInfo))){
      logout();
      return defaultNotifier(
        'Your session has expired, please login again to continue.',
      );
    }
  }


  return axiosInstance({
    headers: {
      'content-type': 'application/json',
      authorization: token ? `Bearer ${token}` : undefined,
    },
    ...params,
  })
    .then((res) => {
      const permissionError = res?.data?.errors?.[0]

      if (res.data
        && res.data.errors
        && res.data.errors[0]
        && res.data.errors[0].message === 'You are not allowed to access this resource') {

        logPermissionDenied(params, permissionError)

        window.location.href = '/403-page';
        defaultNotifier(
          'You are not allowed to access this resource',
        );
      }
      return res;
    })
    .catch(err => (err.response
      ? isTokenExpired(err.response)
      : {
        errors: [
          {
            message: 'Something went wrong.',
          },
        ],
      }));
}


function requestAccessToken(params) {
  const query = {
    mutation: {
      refreshToken: {
        __args: {
          ...params
        },
        ...WORKSPACE_LOGIN,
      },
    },
  };

  const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });

  return axios({
    method: 'POST',
    url: AUTH_BASE,
    data: {
      variables: {},
      query: gqlQuery,
    },
  })
    .then((res) => {
      if(res.data.errors) {
        defaultNotifier(
          'Your session has expired, please login again to continue.',
        );
        return logout();
      }
      
      if(res.data.data) {
        const result = res.data.data.refreshToken;
        
        sessionStorage.setItem('accessToken', result.token)
        sessionStorage.setItem('refreshToken', result.refreshToken)
  
        window.location.reload()
        return res;
      }

    })
    .catch(error => {
      if (error && error.data && error.data.errors) {
        const { message } = error.data.errors[0];
        if (message.includes('jwt expired')
         || message.includes('invalid token')
          || message.includes('jwt must be provided')
          || message.includes('invalid signature')
          || message.includes('You are denied system access')
           || message.includes('jwt malformed')) {
              defaultNotifier(
                'Your session has expired, please login again to continue.',
              );
              return logout(); // Return the logout promise chain
        }
        return error;
      }
    })
}
